/**
 * Tolerant JSON parsing for the Agent Chat API.
 *
 * The backend currently emits **raw control characters inside JSON string
 * literals** — an agent reply containing a blank line arrives as a literal
 * 0x0A byte rather than the `\n` escape RFC 8259 requires. `JSON.parse`
 * rejects that, so `response.json()` throws on most replies.
 *
 * This is a server-side defect and should be fixed there. Until it is, we
 * repair the payload rather than drop the conversation: parse strictly first
 * (zero cost once the server is fixed), and only on failure escape stray
 * control characters and retry.
 */
export function parseJsonLenient(raw) {
    try {
        return { value: JSON.parse(raw), repaired: false };
    }
    catch (strictError) {
        const repaired = escapeControlCharsInStrings(raw);
        if (repaired === raw)
            throw strictError;
        // Let a second failure propagate: the payload is broken beyond this bug.
        return { value: JSON.parse(repaired), repaired: true };
    }
}
/**
 * Escapes unescaped control characters that appear *inside* string literals,
 * leaving structural whitespace between tokens untouched.
 */
function escapeControlCharsInStrings(raw) {
    let out = '';
    let inString = false;
    let escaped = false;
    for (let i = 0; i < raw.length; i++) {
        const char = raw[i];
        if (escaped) {
            out += char;
            escaped = false;
            continue;
        }
        if (char === '\\') {
            out += char;
            escaped = true;
            continue;
        }
        if (char === '"') {
            inString = !inString;
            out += char;
            continue;
        }
        const code = raw.charCodeAt(i);
        if (inString && code < 0x20) {
            out +=
                code === 0x0a ? '\\n'
                    : code === 0x0d ? '\\r'
                        : code === 0x09 ? '\\t'
                            : code === 0x08 ? '\\b'
                                : code === 0x0c ? '\\f'
                                    : `\\u${code.toString(16).padStart(4, '0')}`;
            continue;
        }
        out += char;
    }
    return out;
}
